import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';

type OrganizationItem = {
  id: string;
  name: string;
  type: 'BRAND' | 'AGENCY';
  createdAt: string;
  metrics: {
    memberCount: number;
    jobCount: number;
    totalBudgetRM: number;
    kolCount?: number;
    invitationCount?: number;
  };
};

type OrganizationsResponse = {
  items: OrganizationItem[];
  total: number;
};

export default function AdminAgencies() {
  const [items, setItems] = useState<OrganizationItem[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await api.get<OrganizationsResponse>('/admin/organizations', {
          params: { type: 'AGENCY', search: search || undefined },
        });
        if (!active) return;
        setItems(data.items || []);
      } catch (err: unknown) {
        const e = err as { response?: { data?: { error?: string } } };
        if (!active) return;
        setError(e.response?.data?.error || 'Failed to load agencies');
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [search]);

  const stats = useMemo(() => {
    const totalKols = items.reduce((sum, i) => sum + (i.metrics?.kolCount ?? 0), 0);
    const totalJobs = items.reduce((sum, i) => sum + (i.metrics?.jobCount ?? 0), 0);
    const totalMembers = items.reduce((sum, i) => sum + (i.metrics?.memberCount ?? 0), 0);
    const totalInvites = items.reduce((sum, i) => sum + (i.metrics?.invitationCount ?? 0), 0);
    return {
      total: items.length,
      totalKols,
      totalJobs,
      totalMembers,
      totalInvites,
    };
  }, [items]);

  return (
    <div className="space-y-6 text-glass-text">
      <div>
        <h1 className="page-title">Agency management</h1>
        <p className="text-glass-text-2 text-sm mt-1">
          All agency organizations — team members, KOL roster, managed campaigns, and invitations
        </p>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-500/10 border border-red-500/30 rounded-lg p-3">{error}</div>
      )}

      <div className="grid md:grid-cols-5 gap-4">
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Agencies</div>
          <div className="stat-display text-2xl mt-1">{stats.total}</div>
        </div>
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Team seats</div>
          <div className="stat-display text-2xl mt-1 text-accent2">{stats.totalMembers}</div>
        </div>
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">KOL profiles</div>
          <div className="stat-display text-2xl mt-1 text-[rgb(var(--pos))]">{stats.totalKols}</div>
        </div>
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Managed campaigns</div>
          <div className="stat-display text-2xl mt-1 text-accent2">{stats.totalJobs}</div>
        </div>
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Invitations</div>
          <div className="stat-display text-2xl mt-1 text-[rgb(var(--warn))]">{stats.totalInvites}</div>
        </div>
      </div>

      <div className="glass-card p-4">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search agency name"
          className="glass-input w-full px-4 py-2"
        />
      </div>

      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-glass bg-glass-2/30">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Agency</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Members</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">KOLs</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Campaigns</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Invitations</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Created</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-glass-text-3 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-glass">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-glass-text-2">
                    Loading agencies...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-glass-text-2">
                    No agencies found.
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id} className="hover:bg-glass-3/40 transition-colors">
                    <td className="px-4 py-4">
                      <div className="font-medium text-glass-text">{item.name}</div>
                      <div className="text-xs text-glass-text-3 font-mono truncate max-w-xs">{item.id}</div>
                    </td>
                    <td className="px-4 py-4 text-sm text-glass-text-2">{item.metrics?.memberCount ?? 0}</td>
                    <td className="px-4 py-4 text-sm text-glass-text-2">{item.metrics?.kolCount ?? 0}</td>
                    <td className="px-4 py-4 text-sm text-glass-text-2">{item.metrics?.jobCount ?? 0}</td>
                    <td className="px-4 py-4 text-sm text-glass-text-2">{item.metrics?.invitationCount ?? 0}</td>
                    <td className="px-4 py-4 text-sm text-glass-text-2">
                      {new Date(item.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <Link
                        to={`/admin/agencies/${item.id}`}
                        className="text-sm font-medium text-accent2 hover:underline"
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
